<template>
  <div class="bk-main-canvas relative">
    <HeadFavicon />

    <ClientOnly v-if="user.canAccessToolbar">
      <LazyAdminBar :is-editing />
    </ClientOnly>

    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>

    
  </div>
</template>

<script setup lang="ts">
import { DomainId } from '#graphql-operations'
import { buildFastlyCacheTags } from '~~/server/utils/cacheability'

const route = useRoute()
const user = await useDrupalUser()
const language = useCurrentLanguage()
const { activeDomain, isProduction } = useSiteContext()
const isLocalEnvironment = useRuntimeConfig().public.environment === 'local'

const isEditing = computed(
  () =>
    !!(route.query.blokkliEditing || route.query.blokkliPreview) &&
    user.canAccessToolbar,
)

const { isPartyMode } = usePartyToggle()

const classes = computed(() => {
  return isPartyMode.value ? 'is-party-mode' : ''
})

useHead({
  htmlAttrs: {
    lang: language.value,
    class: classes,
  },
})

useCDNHeaders((v) =>
  v.addTags(buildFastlyCacheTags(['domain:' + activeDomain])).public(),
)

const joaiaOrgId = isProduction
  ? '341b40331f0aa6f0728dd269'
  : '156b427912ad5979cd502042'

const joaiaBaseUrl = isProduction
  ? 'https://web-plugin.joaia.app'
  : 'https://staging.web-plugin.joaia.app'

onMounted(() => {
  if (!user.canAccessToolbar || !window.location.hash) {
    return
  }

  // Vue Router's scrollBehavior fires before the DOM is ready when app.vue
  // has an async setup (await useDrupalUser). Re-scroll once mounted.
  try {
    document.querySelector(window.location.hash)?.scrollIntoView()
  } catch {
    // Invalid CSS selector (e.g. hash starting with a digit).
  }
})

if (activeDomain === DomainId.zuerich_com) {
  // Joaia reads config from document.currentScript. useScript can execute the
  // file without a real script element, so the snippet never boots.
  useHead({
    script: [
      // Staging Joaia starts the card open (mobileStartState: open). DDEV is
      // another origin, so it does not share the minimized state saved on
      // zuerich.com. Set the widget's own flag before its script runs.
      ...(isLocalEnvironment
        ? [
            {
              key: 'joaia-web-plugin-local-collapsed',
              innerHTML: `try{localStorage.setItem("joaiaWebpluginLauncherCollapsed","1");localStorage.removeItem("joaia-webplugin-open")}catch(e){}`,
              tagPriority: -9999,
            },
          ]
        : []),
      {
        key: 'joaia-web-plugin',
        src: 'https://cdn.joaia.app/web-plugin.js',
        defer: true,
        tagPosition: 'bodyClose',
        'data-org-id': joaiaOrgId,
        'data-base-url': joaiaBaseUrl,
      },
    ],
  })
}
</script>

<style>
@reference '~/assets/css/tailwind.css';
/* blökkli overrides for dark mode. */
.bk-html-root {
  @media (prefers-color-scheme: dark) {
    #nuxt-root {
      @apply !bg-black-bis;
      .bk-main-canvas {
        @apply !bg-background;
      }
    }
  }
}

.bk {
  color: black;
}

.bk-library-list-item-inner {
  @apply dark:bg-background;
  &:after {
    @apply dark:hidden;
  }
}

.bk-anchor-indicator {
  @apply relative;
  border-top: 2px solid rgb(var(--bk-theme-red-normal));
}

/* Fokus: compact 3×3 dot grid for Canto crop alignment. */
.bk .bk-blokkli-item-options-item:has(input[name='cropAlignment']) {
  align-items: center;
}

.bk
  .bk-blokkli-item-options-item:has(input[name='cropAlignment'])
  .bk-blokkli-item-options-item-content {
  display: flex;
  align-items: center;
}

.bk .bk-blokkli-item-options-radios:has(input[name='cropAlignment']) {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 5px;
  width: fit-content;
  min-width: 64px;
  height: auto;
  padding: 5px 6px;
  margin-block: auto;
  border: none;
  border-radius: 0;
  background: transparent;
  align-items: center;
  justify-items: center;
}

.bk .bk-blokkli-item-options-radios:has(input[name='cropAlignment']) > label {
  width: 100%;
  height: auto;
  justify-content: center;
}

.bk
  .bk-blokkli-item-options-radios:has(input[name='cropAlignment'])
  > label
  span {
  display: block;
  width: 7px;
  height: 7px;
  padding: 0;
  border-radius: 9999px;
  font-size: 0;
  line-height: 0;
  background-color: rgb(var(--bk-theme-mono-500) / 1);
  border: 1px solid rgb(var(--bk-theme-mono-400) / 1);
  transition:
    background-color 0.15s,
    transform 0.15s;
}

.bk
  .bk-blokkli-item-options-radios:has(input[name='cropAlignment'])
  > label
  input:checked
  + span {
  background-color: rgb(var(--bk-theme-mono-50) / 1);
  border-color: rgb(var(--bk-theme-mono-50) / 1);
  color: rgb(var(--bk-theme-mono-50) / 1);
  transform: scale(1.15);
}

@media not all and (hover: none) {
  .bk
    .bk-blokkli-item-options-radios:has(input[name='cropAlignment'])
    > label:hover
    input:not(:checked)
    + span {
    background-color: rgb(var(--bk-theme-mono-300) / 1);
  }
}
</style>
